/* =============================================================
   AddPartnerModal.jsx — Add partner university modal
   -------------------------------------------------------------
   Single-form flow with inline autocomplete on the name field.

   As the admin types the university name (≥ 2 chars), a debounced
   search runs against the Supabase `universities` table. If a
   match appears the admin can click it → partner is created as
   Connected (linked_university_id set, profile syncs from their
   Settings automatically). If no match or admin types freely →
   partner is created as Manual.

   Autocomplete UX:
     - Dropdown appears below the name input while suggestions exist
     - onMouseDown (not onClick) on items prevents the input blur
       from hiding the dropdown before the click registers
     - onBlur on the input closes the dropdown after 150 ms
     - Linked badge shown inside the input when a university is selected
     - "Unlink" micro-button to go back to free typing
   ============================================================= */

import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

const COUNTRIES = [
  '🇩🇪 Germany', '🇫🇷 France', '🇪🇸 Spain', '🇮🇹 Italy', '🇳🇱 Netherlands',
  '🇸🇪 Sweden', '🇩🇰 Denmark', '🇵🇱 Poland', '🇵🇹 Portugal', '🇧🇪 Belgium',
  '🇬🇧 UK', '🇺🇸 USA', '🇨🇳 China', '🇯🇵 Japan', '🇰🇷 South Korea',
  '🇮🇳 India', '🇧🇷 Brazil', '🇦🇺 Australia', '🇨🇦 Canada', '🇸🇬 Singapore',
  '🇨🇭 Switzerland', '🇦🇹 Austria', '🇨🇿 Czech Republic', '🇭🇺 Hungary',
  '🇳🇴 Norway', '🇫🇮 Finland', '🇮🇪 Ireland', '🇬🇷 Greece',
];

export default function AddPartnerModal({ onAdd, onClose }) {

  /* ── form state ─────────────────────────────────────────── */
  const [form, setForm] = useState({
    name:               '',
    abbr:               '',
    country:            '',
    type:               'BILATERAL',
    linkedUniversityId: null,   // set when user picks from autocomplete
  });
  const [errors,  setErrors]  = useState({});
  const [saving,  setSaving]  = useState(false);
  const [done,    setDone]    = useState(false);

  /* ── autocomplete state ─────────────────────────────────── */
  const [suggestions,     setSuggestions]     = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searching,       setSearching]       = useState(false);
  const searchTimeout = useRef(null);
  const nameInputRef  = useRef(null);

  /* ── close on Escape ────────────────────────────────────── */
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  /* ── focus name input on open ───────────────────────────── */
  useEffect(() => { nameInputRef.current?.focus(); }, []);

  /* ── auto-generate abbreviation from name ───────────────── */
  useEffect(() => {
    if (form.name && !form.linkedUniversityId) {
      const auto = form.name.split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 3).toUpperCase();
      setForm(f => ({ ...f, abbr: auto }));
    }
  }, [form.name]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── debounced Unixplore search ─────────────────────────── */
  useEffect(() => {
    const q = form.name.trim();

    // Don't search if already linked or name too short
    if (form.linkedUniversityId || q.length < 2) {
      clearTimeout(searchTimeout.current);
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      const { data } = await supabase
        .from('universities')
        .select('id, name, country, abbreviation')
        .ilike('name', `%${q}%`)
        .limit(6);
      const results = data ?? [];
      setSuggestions(results);
      setShowSuggestions(results.length > 0);
      setSearching(false);
    }, 300);

    return () => clearTimeout(searchTimeout.current);
  }, [form.name, form.linkedUniversityId]);

  /* ── select a university from the dropdown ──────────────── */
  const selectUniversity = (uni) => {
    setForm(f => ({
      ...f,
      name:               uni.name,
      abbr:               uni.abbreviation
                            ?? uni.name.split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 3).toUpperCase(),
      country:            uni.country ?? f.country,
      linkedUniversityId: uni.id,
    }));
    setSuggestions([]);
    setShowSuggestions(false);
    setErrors(v => ({ ...v, name: '', country: '' }));
  };

  /* ── unlink (go back to free typing) ────────────────────── */
  const unlink = () => {
    setForm(f => ({ ...f, linkedUniversityId: null }));
    nameInputRef.current?.focus();
  };

  /* ── submit ──────────────────────────────────────────────── */
  const handleSubmit = async () => {
    const err = {};
    if (!form.name.trim()) err.name    = 'University name is required';
    if (!form.country)     err.country = 'Country is required';
    if (Object.keys(err).length) { setErrors(err); return; }

    setSaving(true);
    try {
      await onAdd({
        name:               form.name.trim(),
        abbr:               form.abbr,
        country:            form.country,
        type:               form.type,
        connection:         form.linkedUniversityId ? 'Connected' : 'Manual',
        linkedUniversityId: form.linkedUniversityId ?? null,
      });
      setDone(true);
      setTimeout(onClose, 900);
    } catch (e) {
      console.error('[AddPartnerModal]', e);
    } finally {
      setSaving(false);
    }
  };


  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">

        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-[18px] text-indigo-600">add_circle</span>
            <h2 className="font-display text-base font-semibold text-[#0b1c30]">Add Partner University</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">

          {/* ── University Name (with autocomplete) ───────────── */}
          <div className="relative">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1.5">
              University Name <span className="text-red-400">*</span>
            </label>

            <div className="relative">
              <input
                ref={nameInputRef}
                type="text"
                value={form.name}
                onChange={e => {
                  // Typing freely → clear any existing link
                  setForm(f => ({ ...f, name: e.target.value, linkedUniversityId: null }));
                  setErrors(v => ({ ...v, name: '' }));
                }}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                placeholder="e.g. Stockholm School of Economics"
                className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30] transition ${
                  errors.name
                    ? 'border-red-300 bg-red-50'
                    : form.linkedUniversityId
                      ? 'border-emerald-300 bg-emerald-50/30'
                      : 'border-slate-200'
                }`}
              />

              {/* Right-side indicators inside input */}
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 pointer-events-none">
                {searching && (
                  <svg className="animate-spin h-4 w-4 text-slate-300" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                  </svg>
                )}
                {form.linkedUniversityId && (
                  <span className="flex items-center gap-1 text-[11px] font-semibold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full pointer-events-auto">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                    Connected
                  </span>
                )}
              </div>
            </div>

            {/* Linked confirmation + unlink */}
            {form.linkedUniversityId && (
              <p className="text-xs text-emerald-600 mt-1.5 flex items-center gap-1">
                <span className="material-symbols-outlined text-[13px]">check_circle</span>
                Found on Unixplore — profile will sync automatically.
                <button
                  onClick={unlink}
                  className="ml-1 text-slate-400 hover:text-slate-600 underline pointer-events-auto"
                >
                  Unlink
                </button>
              </p>
            )}

            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}

            {/* Autocomplete dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
                {suggestions.map(uni => (
                  <button
                    key={uni.id}
                    onMouseDown={e => { e.preventDefault(); selectUniversity(uni); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-indigo-50 transition-colors text-left"
                  >
                    <div className="w-7 h-7 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-indigo-600 font-bold text-[11px] font-display">
                        {uni.abbreviation?.slice(0, 2) ?? uni.name[0]}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#0b1c30] truncate">{uni.name}</p>
                      <p className="text-xs text-slate-400">{uni.country}</p>
                    </div>
                    <span className="flex items-center gap-1 text-[10px] font-semibold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full flex-shrink-0">
                      <span className="w-1 h-1 rounded-full bg-emerald-500" />
                      On Unixplore
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── Abbreviation ──────────────────────────────────── */}
          <div>
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1.5">
              Abbreviation <span className="text-slate-300 font-normal">(auto-generated)</span>
            </label>
            <input
              type="text"
              value={form.abbr}
              onChange={e => setForm(f => ({ ...f, abbr: e.target.value.toUpperCase().slice(0, 5) }))}
              placeholder="e.g. SSE"
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30] transition font-mono"
            />
          </div>

          {/* ── Country ───────────────────────────────────────── */}
          <div>
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1.5">
              Country <span className="text-red-400">*</span>
            </label>
            <select
              value={form.country}
              onChange={e => { setForm(f => ({ ...f, country: e.target.value })); setErrors(v => ({ ...v, country: '' })); }}
              className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 bg-white text-[#0b1c30] cursor-pointer transition ${
                errors.country ? 'border-red-300 bg-red-50' : 'border-slate-200'
              }`}
            >
              <option value="">Select country</option>
              {/* If linked uni's country isn't in our list, show it anyway */}
              {form.country && !COUNTRIES.includes(form.country) && (
                <option value={form.country}>{form.country}</option>
              )}
              {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            {errors.country && <p className="text-xs text-red-500 mt-1">{errors.country}</p>}
          </div>

          {/* ── Agreement Type ────────────────────────────────── */}
          <div>
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1.5">
              Agreement Type
            </label>
            <select
              value={form.type}
              onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 bg-white text-[#0b1c30] cursor-pointer"
            >
              <option value="BILATERAL">BILATERAL</option>
              <option value="ERASMUS">ERASMUS</option>
              <option value="DOUBLE DEGREE">DOUBLE DEGREE</option>
            </select>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-sm font-medium text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className={`px-5 py-2.5 text-sm font-medium rounded-xl transition-all flex items-center gap-2 disabled:opacity-60 ${
              done
                ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                : 'bg-[#4343d5] text-white hover:bg-indigo-700'
            }`}
          >
            <span className="material-symbols-outlined text-[16px]">
              {done ? 'check_circle' : saving ? 'hourglass_empty' : 'add_circle'}
            </span>
            {done ? 'Added!' : saving ? 'Adding...' : 'Add Partner'}
          </button>
        </div>

      </div>
    </div>
  );
}
